import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Completeness, StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";

export function CasesTab({ patient }) {
  const { user } = useAuth();
  const [cases, setCases] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/cases`, { params: { patient_id: patient.id } }).then((r) => setCases(r.data)).catch(() => setCases([]));
  useEffect(() => { load(); }, [patient.id]); // eslint-disable-line
  const canCreate = ["admin", "doctor", "staff"].includes(user.role);
  const ev = patient.evaluation;
  const create = async () => {
    setBusy(true);
    try { await api.post("/cases", { patient_id: patient.id }); toast.success("Draft generated – awaiting doctor review"); load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid="patient-report-history-list">
      {canCreate && (
        <div className="clinical-card p-5 sm:p-6 flex flex-wrap items-center gap-4 bg-gradient-to-br from-teal-900 via-teal-800 to-slate-900 text-white border-0">
          <Sparkles className="w-6 h-6 text-teal-200" />
          <div className="flex-1 min-w-[220px]"><p className="font-heading font-semibold">Run rules + AI draft</p><p className="text-sm text-teal-100">Applies India-first guideline rules, flags red alerts, and drafts a summary for doctor sign-off. {ev && ev.completeness < 100 && <span className="text-amber-200 font-medium">Data is {ev.completeness}% complete – result will be a PARTIAL assessment.</span>}</p></div>
          <Button data-testid="generate-case-button" disabled={busy} onClick={create} className="bg-white text-teal-900 hover:bg-teal-50 rounded-full">{busy ? "Generating…" : "Generate case"}</Button>
        </div>
      )}
      {cases.length === 0 && <p className="text-sm text-slate-500" data-testid="no-cases">No cases yet.</p>}
      {cases.map((c) => (
        <Link key={c.id} to={`/cases/${c.id}`} data-testid={`case-row-${c.id}`} className={`clinical-card p-6 sm:p-7 flex flex-wrap items-center gap-5 ${c.status !== "released" && ["patient", "nutritionist", "insurance"].includes(user.role) ? "pointer-events-none opacity-70" : "hover:shadow-md"}`}>
          <div className="flex-1 min-w-[220px]"><p className="font-heading font-bold text-lg text-slate-900">{c.title}</p><p className="text-sm text-slate-500 mt-1">{fmtDateTime(c.created_at || c.released_at)}{c.doctor_name && ` · ${c.doctor_name}`}</p>{c.message && <p className="text-xs text-blue-800 mt-1">{c.message}</p>}</div>
          {(c.red_alerts?.length || c.red_alert_count) > 0 && user.role !== "patient" && <span className="badge-pill bg-red-50 text-red-800 border-red-200"><AlertTriangle className="w-3.5 h-3.5" /> {c.red_alerts?.length || c.red_alert_count} red</span>}
          {c.completeness != null && <Completeness value={c.completeness} testId={`case-completeness-${c.id}`} />}
          <StatusBadge status={c.status} /><ArrowRight className="w-4 h-4 text-slate-400" />
        </Link>
      ))}
    </div>
  );
}
